// src/pages/OrderDetails.tsx
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
// Import API functions
import {
    fetchOrderDetails,
    updateOrder,
    getOrderProcessLog,
    downloadOrderPdf,
    sendWhatsAppMessage,
    sendOrderEmail // Import the email function
} from '../utils/api';
import { displayPrice, Modifier, OrderSummary, Customer } from '../utils/types';
import useCancelOrder from '../hooks/useCancelOrder';
import { hasRole } from '../utils/auth';
import { useCustomerContext } from '../context/customerContext';
import { toast } from 'react-toastify';

// Access API URL for constructing the link
const API_URL = window.APP_CONFIG?.API_URL || process.env.REACT_APP_API_URL;

const OrderDetails: React.FC = () => {
    // --- State Variables ---
    const [order, setOrder] = useState<OrderSummary | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const [orderProcessLog, setOrderProcessLog] = useState<any[]>([]);
    const [isDownloadingPdf, setIsDownloadingPdf] = useState<boolean>(false);
    const [isSendingWhatsApp, setIsSendingWhatsApp] = useState<boolean>(false);
    const [isSendingEmail, setIsSendingEmail] = useState<boolean>(false);

    // --- Hooks and Context ---
    const { orderId } = useParams<{ orderId: string }>();
    const { customers } = useCustomerContext();
    const { handleCancelOrder, isCancelling } = useCancelOrder();

    // --- User Info ---
    // Ensure safe parsing and provide default empty object
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userId = user.id; // Will be undefined if user is not found

    // --- Fetch Order Details Effect ---
    useEffect(() => {
        const fetchOrder = async () => {
            setLoading(true);
            setError(null);
            try {
                if (!orderId) {
                    throw new Error('Order ID is missing');
                }
                const orderData = await fetchOrderDetails(parseInt(orderId));
                setOrder(orderData);
            } catch (err: any) {
                setError(err.message || 'Failed to fetch order details');
            } finally {
                setLoading(false);
            }
        };
        fetchOrder();
    }, [orderId]);

    // --- Permission Check Effect ---
    useEffect(() => {
        // Check if order is loaded and userId is available
        if (order && userId && !hasRole(['super_admin', 'staff']) && order.user_id !== userId) {
            setError('You do not have permission to view this order.');
            setLoading(false); // Stop loading if permission denied
        }
    }, [order, userId]); // Add userId as dependency

    // --- Update Order Status in Local State ---
    const updateOrderStatus = (updatedOrder: OrderSummary) => {
        setOrder(prevOrder =>
            prevOrder && prevOrder.id === updatedOrder.id ? { ...prevOrder, status: updatedOrder.status } : prevOrder
        );
    };

    // --- Fetch Order Process Log Effect ---
    useEffect(() => {
        const fetchOrderProcessLog = async () => {
            if (!orderId) return;
            try {
                const response = await getOrderProcessLog(parseInt(orderId));
                setOrderProcessLog(response.process_log || []); // Default to empty array
            } catch (err: any) {
                console.error('Failed to fetch order process log:', err);
                // Optionally set an error state for the log
            }
        };
        fetchOrderProcessLog();
    }, [orderId]);

    // --- Handle Order Processing (Status Update) ---
    const handleProcessOrder = async (status: string) => {
        if (!order || !status || !userId) {
            toast.error("Cannot process order: Missing order details or user ID.");
            return;
        }

        const orderPayload = {
            customerId: order.customer_id,
            orderType: 'order',
            tableId: '1', // Assuming default table ID
            status: status,
            amountToPay: order.balance,
            userId: userId, // Use the logged-in user's ID for the action
            table: [],
            customer_name: customers.find(c => c.id === order.customer_id)?.name || 'Unknown Customer',
        };

        setIsProcessing(true);
        try {
            await updateOrder(order.id, orderPayload);
            updateOrderStatus({ ...order, status: status }); // Update local state
            toast.success(`Order ${order.id} status updated to ${status}!`);
        } catch (error) {
            console.error('Error updating order:', error);
            toast.error(`Failed to process order ${order.id}. Please try again.`);
        } finally {
            setIsProcessing(false);
        }
    };

    // --- Refresh Order Process Log ---
    const refreshOrderProcessLog = async (currentOrderId: string | undefined, setLogState: React.Dispatch<React.SetStateAction<any[]>>) => {
        if (!currentOrderId) return;
        try {
            const response = await getOrderProcessLog(parseInt(currentOrderId));
            setLogState(response.process_log || []); // Update the log state
            toast.success('Process log refreshed!');
        } catch (err: any) {
            console.error('Failed to refresh process log:', err);
            toast.error('Failed to refresh process log. Please try again.');
        }
    };

    // --- Handle PDF Download ---
    const handleDownloadPdf = async () => {
        if (!order?.order_uuid) {
            toast.error("Order UUID is missing.");
            return;
        }
        setIsDownloadingPdf(true);
        try {
            // Directly fetch the PDF using the new route
            const pdfUrl = `${API_URL}/orders/${order.order_uuid}/pdf`;
            const response = await fetch(pdfUrl, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                }
            });

            if (!response.ok) {
                // Try to parse error message from JSON
                let errorMsg = `Failed to download PDF: ${response.statusText}`;
                try {
                    const errorData = await response.json();
                    errorMsg = errorData.message || errorMsg;
                } catch { }
                throw new Error(errorMsg);
            }

            const pdfBlob = await response.blob();
            const url = window.URL.createObjectURL(pdfBlob);

            // const link = document.createElement('a');
            // link.href = url;
            // link.setAttribute('download', `order_${order.order_uuid}.pdf`);
            // document.body.appendChild(link);
            // link.click();
            // link.parentNode?.removeChild(link);
            // window.URL.revokeObjectURL(url);
            // toast.success("PDF download started.");

            // Open PDF in a new tab
            window.open(url, '_blank');

            // Optionally, revoke the object URL after some time
            setTimeout(() => window.URL.revokeObjectURL(url), 10000);

            toast.success("PDF opened in new tab.");

        } catch (error: any) {
            console.error('Error downloading PDF:', error);
            toast.error(`Failed to download PDF: ${error.message || 'Check console for details.'}`);
        } finally {
            setIsDownloadingPdf(false);
        }
    };

    // --- Handle Sending WhatsApp Link ---
    const handleSendWhatsAppLink = async () => {
        if (!orderId || !order) {
            toast.error("Order details are missing for WhatsApp.");
            return;
        }

        const customerForNotification = customers.find(c => c.id === order.customer_id);
        const targetPhoneNumber = customerForNotification?.phone;

        if (!targetPhoneNumber) {
            toast.warn(`Cannot send WhatsApp: Phone number for customer ${customerForNotification?.name || order.customer_id} not found.`);
            return;
        }

        const pdfLink = `${API_URL}/orders/${order?.order_uuid}/pdf`;

        // Ensure phone number format is correct for your venom-bot service
        // const targetPhoneNumber = '601112163808'; //'60188748808'; // Example, adjust as needed
        if (!order?.order_uuid) {
            toast.error("Order UUID is missing, cannot generate PDF link for WhatsApp.");
            return;
        }

        setIsSendingWhatsApp(true);
        try {
            const settings = JSON.parse(localStorage.getItem('settings') || '{}');

            const whatsappMessage = `*${settings.shortname || 'Our Store'}* sent you a document.\n\n\nOrder *${orderId}* PDF is ready.\n\nDownload here: ${pdfLink}\n\n\n*Note: This is a bot message, please do not reply.*\n\n*Please contact ${settings.shortname || 'Us'} at ${settings.phone || 'our contact number'} instead.*`;

            await sendWhatsAppMessage(targetPhoneNumber, whatsappMessage);
            // toast.info(`WhatsApp link sent to ${targetPhoneNumber}.`);
            toast.info(`Order PDF link sent via WhatsApp to ${customerForNotification.name} (${targetPhoneNumber}).`);


        } catch (whatsappError: any) {
            console.error('Failed to send WhatsApp message:', whatsappError);
            toast.error(`Failed to send WhatsApp link: ${whatsappError.message}`);
        } finally {
            setIsSendingWhatsApp(false);
        }
    };


    // const pdfLink = `${API_URL}/orders/${orderId}/pdf`;
    // const pdfLink = `${API_URL}/orders/${order?.order_uuid}/pdf`;
    // const targetPhoneNumber = '601112163808'; // Ensure correct format
    //     const settings = JSON.parse(localStorage.getItem('settings') || '{}');
    //     const whatsappMessage = `*${settings.shortname}*\n\nOrder *${orderId}* PDF is ready.\n\nDownload here: ${pdfLink}`;


    // --- Handle Sending Order Email ---
    const handleSendEmail = async () => {
        if (!orderId || !order) {
            toast.error("Order details are missing for Email.");
            return;
        }

        const customerForNotification = customers.find(c => c.id === order.customer_id);
        const recipientEmail = customerForNotification?.email;

        if (!recipientEmail) {
            toast.warn(`Cannot send email: Email address for customer ${customerForNotification?.name || order.customer_id} not found.`);
            return;
        }

        setIsSendingEmail(true);
        try {
            const response = await sendOrderEmail(parseInt(orderId), recipientEmail);
            toast.success(response.message || `Order confirmation email sent to ${customerForNotification.name} (${recipientEmail}).`);
        } catch (emailError: any) {
            console.error('Failed to send order email:', emailError);
            toast.error(`Failed to send email: ${emailError.message}`);
        } finally {
            setIsSendingEmail(false);
        }
    };


    // --- Loading/Error/Not Found States ---
    if (loading) return <div className="container mx-auto p-4 text-center">Loading order details...</div>;
    if (error) return <div className="container mx-auto p-4 text-red-500 text-center">Error: {error}</div>;
    if (!order) return <div className="container mx-auto p-4 text-center">Order not found.</div>;

    // --- Determine if any background action is in progress ---
    const isActionInProgress = isDownloadingPdf || isSendingWhatsApp || isSendingEmail || isProcessing || isCancelling;

    // --- Render Component ---
    return (
        <div className="container mx-auto p-4">
            <h1 className="text-2xl font-bold mb-4">Order Details</h1>

            {/* --- Action Buttons Row 1 (Download/Send) --- */}
            <div className="mb-4 flex flex-wrap gap-2">
                <button
                    onClick={handleDownloadPdf}
                    disabled={isActionInProgress}
                    className={`bg-purple-600 hover:bg-purple-700 text-white font-bold py-1 px-3 rounded transition duration-150 ${isActionInProgress ? 'opacity-50 cursor-not-allowed' : ''}`}
                    title="Download the order details as a PDF file."
                >
                    {isDownloadingPdf ? 'Downloading...' : 'Download PDF'}
                </button>

                <button
                    onClick={handleSendWhatsAppLink}
                    disabled={isActionInProgress}
                    className={`bg-teal-600 hover:bg-teal-700 text-white font-bold py-1 px-3 rounded transition duration-150 ${isActionInProgress ? 'opacity-50 cursor-not-allowed' : ''}`}
                    title="Send a WhatsApp message with a link to the PDF."
                >
                    {isSendingWhatsApp ? 'Sending WhatsApp...' : 'Send WhatsApp to Customer'}
                </button>

                <button
                    onClick={handleSendEmail}
                    disabled={isActionInProgress}
                    className={`bg-cyan-600 hover:bg-cyan-700 text-white font-bold py-1 px-3 rounded transition duration-150 ${isActionInProgress ? 'opacity-50 cursor-not-allowed' : ''}`}
                    title="Send the order details via email."
                >
                    {isSendingEmail ? 'Sending Email...' : 'Send Email to Customer'}
                </button>
            </div>

            {/* --- Action Buttons Row 2 (Status Changes) --- */}
            <div className="mb-4 flex flex-wrap gap-2">
                {hasRole(['super_admin', 'staff']) && (
                    <button
                        className={`text-white bg-blue-600 px-4 py-1 rounded-lg ${isActionInProgress ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'}`}
                        onClick={async () => {
                            await handleProcessOrder('pending');
                            await refreshOrderProcessLog(orderId, setOrderProcessLog);
                        }}
                        disabled={isActionInProgress}
                        title="Mark the order status as Pending."
                    >
                        {isProcessing && order.status === 'pending' ? 'Pending...' : 'Set Pending'}
                    </button>
                )}
                {hasRole(['super_admin', 'staff']) && (
                    <button
                        className={`text-white bg-green-600 px-4 py-1 rounded-lg ${isActionInProgress ? 'opacity-50 cursor-not-allowed' : 'hover:bg-green-700'}`}
                        onClick={async () => {
                            await handleProcessOrder('processing');
                            await refreshOrderProcessLog(orderId, setOrderProcessLog);
                        }}
                        disabled={isActionInProgress}
                        title="Mark the order status as Processing."
                    >
                        {isProcessing && order.status === 'processing' ? 'Processing...' : 'Set Processing'}
                    </button>
                )}
                {order.status !== 'cancel' && (
                    <button
                        onClick={() => handleCancelOrder(order.id, updateOrderStatus)}
                        className={`text-white bg-red-600 px-4 py-1 rounded-lg ${isActionInProgress ? 'opacity-50 cursor-not-allowed' : 'hover:bg-red-700'}`}
                        disabled={isActionInProgress}
                        title="Cancel this order."
                    >
                        {isCancelling ? 'Cancelling...' : 'Cancel Order'}
                    </button>
                )}
            </div>

            {/* --- Order Process Log --- */}
            {hasRole(['super_admin', 'staff']) && (
                <div className="md:fixed md:top-10 md:right-2 bg-pink-100 mb-4 p-4 border border-2 border-pink-200 max-w-sm rounded-lg shadow">
                    {orderProcessLog && orderProcessLog.length > 0 ? (
                        <div>
                            <h2 className="text-sm font-semibold mb-2">Order Process Log:</h2>
                            <button
                                className="text-white bg-blue-600 px-2 py-1 rounded-lg mb-2 text-xs hover:bg-blue-700"
                                onClick={() => refreshOrderProcessLog(orderId, setOrderProcessLog)}
                                title="Refresh the process log."
                            >
                                Refresh Log
                            </button>
                            <ul className="max-h-60 overflow-y-auto text-xs space-y-1">
                                {orderProcessLog
                                    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
                                    .map((log, index) => (
                                        <li key={index} className={`${index === 0 ? 'font-bold text-pink-700' : 'text-gray-700'}`}>
                                            <span>
                                                {new Date(log.timestamp).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true })}&nbsp;|
                                                &nbsp;{log.action} - by {log.user_id}:{log.user_name}
                                            </span>
                                        </li>
                                    ))}
                            </ul>
                        </div>
                    ) : (
                        <p className="text-sm text-gray-600">No process log available.</p>
                    )}
                </div>
            )}

            {/* --- Order Info Section --- */}
            <div className="mb-6 p-4 border rounded-lg bg-gray-50">
                <h2 className="text-xl font-semibold mb-3">Order Summary</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2 text-sm">
                    <p><span className="font-semibold text-gray-700">Order ID:</span> {order.id}</p>
                    <p>
                        <span className="font-semibold text-gray-700">Customer:</span>
                        &nbsp;{order.customer_id}&nbsp;:&nbsp;
                        {customers.find(c => c.id === order.customer_id)?.name || 'N/A'}
                        &nbsp;&lt;{customers.find(c => c.id === order.customer_id)?.email || 'N/A'}&gt;
                    </p>
                    <p><span className="font-semibold text-gray-700">Order Date:</span> {new Date(order.created_at).toLocaleString()}</p>
                    <p>
                        <span className="font-semibold text-gray-700">Status:</span>
                        <span className={`ml-1 font-bold
                            ${order.status === 'pending' ? 'text-blue-600'
                                : order.status === 'processing' ? 'text-yellow-600'
                                    : order.status === 'completed' ? 'text-green-600'
                                        : order.status === 'cancel' ? 'text-red-600'
                                            : 'text-gray-700'
                            }`}>
                            {order.status.toUpperCase()}
                        </span>
                    </p>
                    <p className="md:col-span-2"><span className="font-semibold text-gray-700">Total Amount:</span> <span className="font-bold text-lg text-green-700">{displayPrice(Number(order.balance))}</span></p>
                </div>
            </div>

            {/* --- Order Items Section --- */}
            <h2 className="text-xl font-bold mb-3">Order Items ({order.items.length})</h2>
            <ol className="list-decimal list-inside space-y-4">
                {order.items.map((item, index) => (
                    <li key={index} className="p-4 border rounded-lg bg-white shadow-sm before:content-[counter(my-counter)] before:mr-3 before:font-bold" style={{ counterIncrement: 'my-counter' }}>
                        <div className="flex flex-col sm:flex-row justify-between">
                            <div className="flex-grow mb-2 sm:mb-0">
                                <p className="font-medium text-base">
                                    <Link to={`/product/${item.product_id}`} target='_blank' className="text-blue-600 hover:underline" title={`View product: ${item.payment_for}`}>
                                        {item.payment_for} {/* Product Name */}
                                    </Link>
                                    <span className="text-xs text-gray-500 ml-2">(SKU: {item.payment_desc})</span>
                                </p>
                                {/* Modifier display logic */}
                                {typeof item.modifiers === 'string' && (item.modifiers = JSON.parse(item.modifiers))}
                                {item.modifiers && item.modifiers.length > 0 && (
                                    <div className='ml-5 mt-1'>
                                        <ul className="list-disc list-inside text-xs text-gray-600 space-y-1">
                                            {item.modifiers.map((modifier: Modifier) => (
                                                <li key={modifier.id}>
                                                    {modifier.product_name}
                                                    <span className="text-gray-500 ml-1">(SKU: {modifier.product_code})</span>
                                                    &nbsp;(+{displayPrice(modifier.product_value)})
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                            <div className="text-sm text-gray-700 sm:text-right">
                                <p>{displayPrice(item.unit_price)} x {item.qty}</p>
                                <p className="font-semibold text-base">{displayPrice(item.unit_price * item.qty)}</p>
                            </div>
                        </div>
                    </li>
                ))}
            </ol>
        </div>
    );
};

export default OrderDetails;
